import { Button } from "@mui/material";
import { cn } from "components/lib/utils";
import { Case, FormSection, SectionReviewState, SectionValueView, ValidationStatus } from "generated/graphql";
import { useSectionValues } from "./useSectionValues";
import { REVIEW_STATE_UI } from "./REVIEW_STATE_UI";
import { AssigneeChipWithPopover } from "./AssigneeChip";
import { useNavigate } from "react-router";
import { AppRoutes } from "AppRoutes";
import { DisplaySectionOutcome, DisplayValidationIssue } from "./DisplaySectionOutcome";
import { SectionValueAssignment } from "./SectionValueAssignment";
import { SectionFromId } from "./SectionFromId";

function Progress({ sectionsComplete, outOf }: { sectionsComplete: number; outOf: number }) {
  const sections = Array.from({ length: outOf }, (_, index) => index);
  return (
    <div className="flex flex-nowrap gap-1">
      {sections.map((section, index) => (
        <div
          key={section}
          className={cn("h-3 grow shrink", sectionsComplete > index ? "bg-secondary-500" : "bg-gray-100")}
        />
      ))}
    </div>
  );
}

function sectionNameFromValue(sectionValue: SectionValueView, formSections: FormSection[]): string {
  return formSections.find((fs) => fs.id === sectionValue.sectionId)?.name ?? "";
}

function ReviewState({
  caseId,
  lensId,
  reviewState,
  sectionValues,
  formSections,
  formId,
}: {
  caseId: number;
  lensId: number;
  reviewState: SectionReviewState;
  sectionValues: SectionValueView[];
  formSections: FormSection[];
  formId: number;
}) {
  return sectionValues.length > 0 ? (
    <div className="flex flex-col gap-2 items-start">
      <div className="flex items-center gap-2">
        <p className="uppercase font-bold">{REVIEW_STATE_UI[reviewState].name}</p>
        <p className="text-gray-100">•</p>
        <p className="font-bold text-gray-400">{sectionValues.length}</p>
      </div>
      <div
        className={cn(
          REVIEW_STATE_UI[reviewState].color,
          "rounded-lg p-4 border border-gray-100 w-full flex flex-col gap-5"
        )}>
        {sectionValues.map((sv, index) => (
          <SectionFromId sectionId={sv.sectionId} key={sv.sectionId}>
            {(section) => (
              <div className="flex flex-col gap-2 w-full">
                <div className="flex justify-between items-center">
                  <a
                    className="text-sm font-bold hover:underline cursor-pointer"
                    href={AppRoutes.caseProfileSection({
                      lensId,
                      caseId,
                      sectionId: sv.sectionId,
                      formPageId: section?.formPageId,
                    })}>
                    {sectionNameFromValue(sv, formSections)}
                  </a>
                  <div className="flex gap-2">
                    {[SectionReviewState.Complete, SectionReviewState.Review].includes(sv.reviewState) && (
                      <SectionValueAssignment sectionValue={sv}>
                        {({ userIsOverallAssignee, showQAInfo, assignee }) => (
                          <AssigneeChipWithPopover
                            formId={formId}
                            user={assignee}
                            isMe={userIsOverallAssignee}
                            isQa={showQAInfo}
                            caseId={caseId}
                            sectionId={sv.sectionId}
                          />
                        )}
                      </SectionValueAssignment>
                    )}
                    {[SectionReviewState.Complete, SectionReviewState.Validation].includes(sv.reviewState) && (
                      <SectionValueAssignment sectionValue={sv}>
                        {({ userIsQAAssignee, showQAInfo, qaAssignee }) => (
                          <AssigneeChipWithPopover
                            formId={formId}
                            user={qaAssignee}
                            isMe={userIsQAAssignee}
                            isQa={showQAInfo}
                            caseId={caseId}
                            sectionId={sv.sectionId}
                          />
                        )}
                      </SectionValueAssignment>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <DisplaySectionOutcome outcomeStatus={sv.outcomeStatus} />
                  {sv.validationStatus === ValidationStatus.Issue && (
                    <>
                      <p className="text-gray-100">•</p>
                      <DisplayValidationIssue validationStatus={sv.validationStatus} />
                    </>
                  )}
                </div>
                {index !== sectionValues.length - 1 && <div className="border-b border-gray-100 pb-4" />}
              </div>
            )}
          </SectionFromId>
        ))}
      </div>
    </div>
  ) : (
    <></>
  );
}

export default function SBR({
  formId,
  lensCase,
  sectionValues,
  formSections,
}: {
  formId: number;
  lensCase: Case;
  sectionValues: SectionValueView[];
  formSections: FormSection[];
}) {
  const svStats = useSectionValues(sectionValues);
  const navigate = useNavigate();
  const goToProfile = () => {
    navigate(AppRoutes.caseDetailBuilder(lensCase.lensId, lensCase.id, "profile"));
  };

  return (
    <div className="flex flex-col gap-4 pb-3">
      <div className="flex flex-col gap-4 px-4 pt-4">
        <Progress sectionsComplete={svStats.completedSections.length} outOf={formSections.length} />
        <div className="flex items-center">
          <p className="font-extrabold mr-2">
            {svStats.completedSections.length}/{formSections.length}
          </p>
          <p className="text-sm">Sections Complete</p>
          <div className="grow" />
          <Button variant="contained" onClick={goToProfile}>
            View Profile
          </Button>
        </div>
      </div>
      <div className=" px-4">
        <div className="border-b border-gray-50" />
      </div>
      <div className="overflow-auto max-h-96 flex flex-col gap-4 px-4 pb-4">
        <ReviewState
          caseId={lensCase.id}
          lensId={lensCase.lensId}
          formId={formId}
          sectionValues={svStats.reviewSections}
          formSections={formSections}
          reviewState={SectionReviewState.Review}
        />
        <ReviewState
          caseId={lensCase.id}
          lensId={lensCase.lensId}
          formId={formId}
          sectionValues={svStats.qaSections}
          formSections={formSections}
          reviewState={SectionReviewState.Validation}
        />
        <ReviewState
          caseId={lensCase.id}
          lensId={lensCase.lensId}
          formId={formId}
          sectionValues={svStats.completedSections}
          formSections={formSections}
          reviewState={SectionReviewState.Complete}
        />
      </div>
    </div>
  );
}
